import {
  dataPointPool,
  normalizeChartDataPoint,
  normalizeChartDataPoints,
  type ChartDataInputPoint,
  type ChartDataPoint,
} from "./data";
import type { Timeframe } from "./types";

export type LiveSymbol = {
  display_name?: string;
  name?: string;
  symbol?: string;
  precision?: number;
  pip?: number;
  is_active?: boolean;
  exchange_is_open?: number;
  market?: string;
  subgroup?: string;
};

export type LiveDataOptions = {
  url: string;
  symbol: string;
  pipSize: number;
  maxDataPoints: number;
  reconnectMs: number;
  historyCount: number;
  historyStart?: string | number;
  historyEnd?: string | number;
  authToken?: string;
  authRequest?: Record<string, unknown>;
  subscribeRequest?: Record<string, unknown>;
  historyRequest?: Record<string, unknown>;
};

export type LiveDataFeed = {
  configure(config: Partial<LiveDataOptions>): void;
  updateForTimeframe(timeframe: Timeframe): void;
  ensureDataForTimeframe(timeframe: Timeframe): Promise<void>;
  requestHistoryBackfill?(beforeEpochMs: number, timeframe: Timeframe, windowMs: number): void;
  start(): void;
  stop(): void;
  addListener(callback: (data: ChartDataPoint[]) => void): void;
  removeListener(callback: (data: ChartDataPoint[]) => void): void;
  addHistoryListener?(callback: (data: ChartDataPoint[], mode: "replace" | "prepend") => void): void;
  removeHistoryListener?(callback: (data: ChartDataPoint[], mode: "replace" | "prepend") => void): void;
  addMarketListener(callback: () => void): void;
  removeMarketListener(callback: () => void): void;
  getSymbols(): LiveSymbol[];
  getSelectedSymbol(): string;
  selectSymbol(symbol: string): void;
  getData(): ChartDataPoint[];
  getLatestTick(): ChartDataPoint | null;
};

export type LiveDataAdapter = LiveDataFeed & {
  receive(raw: unknown): void;
  setData(points: Array<ChartDataInputPoint | ChartDataPoint>): void;
  append(point: ChartDataInputPoint | ChartDataPoint): void;
  appendMany(points: Array<ChartDataInputPoint | ChartDataPoint>): void;
  clearData(): void;
  setSymbols(symbols: LiveSymbol[]): void;
};

const DEFAULT_WS_URL = "wss://hft.safarigari.com";
const DEFAULT_SYMBOL = "VIX_10";
function roundTo(price: number, pipSize: number): number {
  return parseFloat(price.toFixed(pipSize));
}

function toObject(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : null;
}

function toNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function decimalPlacesFromPip(pip: number | undefined): number | null {
  if (!pip || !Number.isFinite(pip)) return null;
  const text = String(pip);
  if (!text.includes(".")) return 0;
  return text.split(".")[1]?.replace(/0+$/, "").length ?? 0;
}

class LiveDataManager implements LiveDataFeed {
  private data: ChartDataPoint[] = [];
  private config: LiveDataOptions;
  private listeners: ((data: ChartDataPoint[]) => void)[] = [];
  private historyListeners: ((data: ChartDataPoint[], mode: "replace" | "prepend") => void)[] = [];
  private marketListeners: (() => void)[] = [];
  private socket: WebSocket | null = null;
  private reconnectTimer: number | null = null;
  private manuallyStopped = false;
  private suppressReconnectOnce = false;
  private symbols: LiveSymbol[] = [];
  private shouldSubscribeAfterSymbols = true;
  private awaitingAuthResponse = false;
  private historyFallbackTried = false;
  private pendingHistoryMode: "replace" | "prepend" | null = null;
  private marketTransitionAnchorEpoch: number | null = null;
  private marketEpochOffset: number | null = null;
  private marketTransitionAnchorQuote: number | null = null;
  private marketQuoteOffset: number | null = null;

  constructor(config?: Partial<LiveDataOptions>) {
    dataPointPool.preAllocate(2000);
    this.config = {
      url: DEFAULT_WS_URL,
      symbol: DEFAULT_SYMBOL,
      pipSize: 3,
      maxDataPoints: this.getMaxDataPointsForTimeframe("1m"),
      reconnectMs: 2500,
      historyCount: 1000,
      ...config,
    };
  }

  configure(config: Partial<LiveDataOptions>): void {
    const shouldReconnect =
      Boolean(this.socket) &&
      (config.url !== undefined ||
        config.symbol !== undefined ||
        config.authToken !== undefined ||
        config.authRequest !== undefined ||
        config.subscribeRequest !== undefined ||
        config.historyRequest !== undefined ||
        config.historyCount !== undefined ||
        config.historyStart !== undefined ||
        config.historyEnd !== undefined);

    this.config = { ...this.config, ...config };
    if (shouldReconnect) {
      this.stop();
      this.start();
    }
  }

  private getRequiredDataPointsForTimeframe(timeframe: Timeframe): number {
    const requiredCandles = 200;
    const candleSeconds: Record<Timeframe, number> = {
      "1t": 1,
      "1m": 60,
      "2m": 120,
      "3m": 180,
      "5m": 300,
      "10m": 600,
      "15m": 900,
      "30m": 1800,
      "1h": 3600,
      "2h": 7200,
      "4h": 14400,
      "8h": 28800,
      "1D": 86400,
    };
    return requiredCandles * candleSeconds[timeframe];
  }

  private getMaxDataPointsForTimeframe(timeframe: Timeframe): number {
    const required = this.getRequiredDataPointsForTimeframe(timeframe);
    return Math.min(required * 2, 500000);
  }

  updateForTimeframe(timeframe: Timeframe): void {
    const nextMax = this.getMaxDataPointsForTimeframe(timeframe);
    if (nextMax === this.config.maxDataPoints) return;
    this.config.maxDataPoints = nextMax;
    this.trimData();
  }

  async ensureDataForTimeframe(_timeframe: Timeframe): Promise<void> {
    // Historical backfill is server-dependent. Live ticks stream in as they arrive.
  }

  start(): void {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) return;
    this.manuallyStopped = false;
    this.historyFallbackTried = false;
    this.clearReconnectTimer();

    let socket: WebSocket;
    try {
      socket = new WebSocket(this.config.url);
      this.socket = socket;
    } catch (error) {
      console.warn("ChartMain live data connection failed", error);
      this.scheduleReconnect();
      return;
    }

    socket.onopen = () => {
      if (this.socket !== socket) return;
      this.shouldSubscribeAfterSymbols = !this.config.authRequest && !this.config.authToken;
      this.send({ request: "ping" });
      this.send({ request: "symbols" });
      this.startAuthenticatedStream();
    };
    socket.onmessage = (event) => {
      if (this.socket === socket) this.handleMessage(event.data);
    };
    socket.onerror = () => {
      // The close event will drive reconnects. Keep this quiet for embedders.
    };
    socket.onclose = () => {
      if (this.socket !== socket) return;
      this.socket = null;
      if (this.suppressReconnectOnce) {
        this.suppressReconnectOnce = false;
        return;
      }
      if (!this.manuallyStopped) this.scheduleReconnect();
    };
  }

  stop(): void {
    this.manuallyStopped = true;
    this.clearReconnectTimer();
    this.socket?.close();
    this.socket = null;
  }

  private startAuthenticatedStream(): void {
    if (this.config.authRequest) {
      this.awaitingAuthResponse = true;
      this.send(this.config.authRequest);
      return;
    }
    if (this.config.authToken) {
      this.awaitingAuthResponse = true;
      this.send({ request: "auth", token: this.config.authToken });
      return;
    }
  }

  private restartConnection(): void {
    this.clearReconnectTimer();
    if (this.socket) {
      const socket = this.socket;
      this.socket = null;
      socket.close();
    }
    if (!this.manuallyStopped) this.start();
  }

  private resolveMarketEpochOffset(rawLatestEpoch: number): void {
    if (this.marketTransitionAnchorEpoch === null || this.marketEpochOffset !== null) return;
    this.marketEpochOffset = this.marketTransitionAnchorEpoch - rawLatestEpoch;
  }

  private resolveMarketQuoteOffset(rawLatestQuote: number): void {
    if (this.marketTransitionAnchorQuote === null || this.marketQuoteOffset !== null) return;
    this.marketQuoteOffset = this.marketTransitionAnchorQuote - rawLatestQuote;
  }

  private getAdjustedEpoch(epoch: number): number {
    if (this.marketTransitionAnchorEpoch === null) return epoch;
    if (this.marketEpochOffset === null) {
      this.marketEpochOffset = this.marketTransitionAnchorEpoch - epoch;
    }
    return epoch + this.marketEpochOffset;
  }

  private getAdjustedQuote(quote: number): number {
    if (this.marketTransitionAnchorQuote === null) return quote;
    if (this.marketQuoteOffset === null) {
      this.marketQuoteOffset = this.marketTransitionAnchorQuote - quote;
    }
    return quote + this.marketQuoteOffset;
  }

  private subscribe(): void {
    this.send(this.config.subscribeRequest ?? { request: "subscribe", symbol: this.config.symbol });
  }

  private requestHistory(): void {
    this.pendingHistoryMode = "replace";
    const payload: Record<string, unknown> = this.config.historyRequest ?? {
      request: "ticks_history",
      type: "ticks",
      symbol: this.config.symbol,
    };
    if (this.config.historyRequest) {
      this.send(payload);
      return;
    }

    const start = this.normalizeHistoryBoundary(this.config.historyStart);
    const end = this.normalizeHistoryBoundary(this.config.historyEnd);
    if (start !== undefined) payload.start = start;
    if (end !== undefined) payload.end = end;

    if (start !== undefined || end !== undefined) {
      payload.limit = this.config.historyCount;
    } else {
      payload.count = this.config.historyCount;
    }
    this.send(payload);
  }

  private requestRecentHistory(): void {
    this.pendingHistoryMode = "replace";
    const payload: Record<string, unknown> = this.config.historyRequest ?? {
      request: "ticks_history",
      type: "ticks",
      symbol: this.config.symbol,
      count: this.config.historyCount,
    };
    this.send(payload);
  }

  requestHistoryBackfill(beforeEpochMs: number, _timeframe: Timeframe, windowMs: number): void {
    if (this.config.historyRequest) return;
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    if (this.pendingHistoryMode !== null) return;
    if (this.data.length === 0) return;

    const beforeMs = Number.isFinite(beforeEpochMs) ? Math.floor(beforeEpochMs) : Date.now();
    const backfillCount = Math.max(
      this.config.historyCount,
      Math.ceil(windowMs / 1000),
    );
    // Move the boundary just before the current earliest candle so the API
    // returns older history instead of overlapping the same second.
    const end = Math.max(0, beforeMs - 1);

    this.pendingHistoryMode = "prepend";
    this.config.maxDataPoints = Math.min(
      500000,
      Math.max(this.config.maxDataPoints, this.data.length + 5000),
    );

    this.send({
      request: "ticks_history",
      symbol: this.config.symbol,
      type: "ticks",
      end,
      count: backfillCount,
    });
  }

  private normalizeHistoryBoundary(value: string | number | undefined): number | undefined {
    if (value === undefined) return undefined;
    if (typeof value === "number") return value < 10_000_000_000 ? value * 1000 : value;
    const trimmed = value.trim();
    const numeric = Number(trimmed);
    if (Number.isFinite(numeric)) return numeric < 10_000_000_000 ? numeric * 1000 : numeric;

    const dateParts = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (dateParts) {
      const [, day, month, year] = dateParts;
      return Date.UTC(Number(year), Number(month) - 1, Number(day));
    }

    const parsed = Date.parse(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private send(payload: Record<string, unknown>): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify(payload));
  }

  private logPendingHistoryMessage(message: Record<string, unknown>): void {
    if (this.pendingHistoryMode === null) return;

    const data = toObject(message.data);
    const nestedMessage = toObject(message.message);

    console.debug("[ChartMain] history message", {
      mode: this.pendingHistoryMode,
      status: message.status,
      code: message.code,
      keys: Object.keys(message),
      dataKeys: data ? Object.keys(data) : null,
      messageKeys: nestedMessage ? Object.keys(nestedMessage) : null,
    });
  }

  private handleMessage(raw: unknown): void {
    const text = typeof raw === "string" ? raw : "";
    if (!text) return;

    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      return;
    }

    const message = toObject(parsed);
    if (!message) return;

    this.logPendingHistoryMessage(message);

    if (message.status === "ok") {
      if (Array.isArray(message.data)) {
        this.symbols = message.data.filter(Boolean) as LiveSymbol[];
        this.applySymbolPrecision();
        this.notifyMarketListeners();
        if (this.shouldSubscribeAfterSymbols) {
          this.shouldSubscribeAfterSymbols = false;
          this.requestHistory();
          this.subscribe();
        }
        return;
      }
      
      if (Array.isArray(message.message)) {
        this.symbols = message.message.filter(Boolean) as LiveSymbol[];
        this.applySymbolPrecision();
        this.notifyMarketListeners();
        if (this.shouldSubscribeAfterSymbols) {
          this.shouldSubscribeAfterSymbols = false;
          this.requestHistory();
          this.subscribe();
        }
        return;
      }
    }

    if (this.awaitingAuthResponse && message.status === "ok") {
      this.awaitingAuthResponse = false;
      this.requestHistory();
      this.subscribe();
      return;
    }

    if (message.status === "error") {
      console.warn("ChartMain live data error:", message.message ?? message);
      const errorText = String(message.message ?? "");
      const hasRequestedRange =
        this.config.historyStart !== undefined ||
        this.config.historyEnd !== undefined;
      const shouldFallback =
        !this.config.historyRequest &&
        !this.historyFallbackTried &&
        hasRequestedRange &&
        message.code === 404 &&
        /no tick data/i.test(errorText);

      if (shouldFallback) {
        this.historyFallbackTried = true;
        this.requestRecentHistory();
        return;
      }
      this.pendingHistoryMode = null;
      return;
    }

    if (this.parseHistory(message)) return;

    const tick = this.parseTick(message);
    if (tick) this.addTick(tick);
  }

  private applySymbolPrecision(): void {
    const symbol = this.symbols.find((item) => item.symbol === this.config.symbol || item.name === this.config.symbol);
    if (!symbol) return;
    const precision = toNumber(symbol.precision);
    const pipDecimals = decimalPlacesFromPip(toNumber(symbol.pip) ?? undefined);
    this.config.pipSize = precision ?? pipDecimals ?? this.config.pipSize;
  }

  private parseTick(message: Record<string, unknown>): ChartDataPoint | null {
    // First, check if this is a HFT19 tick message
    // HFT19 format: { "type": "tick", "symbol": "VIX_100", "price": 12345.67, "epoch_ms": 1716930000000 }
    if (message.type === "tick") {
      const symbol = String(message.symbol ?? this.config.symbol);
      if (symbol !== this.config.symbol) return null;
      
      const quote = toNumber(message.price);
      const epochRaw = toNumber(message.epoch_ms) ?? toNumber(message.epoch);
      
      if (quote === null) return null;
      
      const epochBase = epochRaw ? Math.floor(epochRaw > 10_000_000_000 ? epochRaw / 1000 : epochRaw) : Math.floor(Date.now() / 1000);
      const epoch = this.getAdjustedEpoch(epochBase);
      const precision = toNumber(message.precision) ?? toNumber(message.pip_size) ?? this.config.pipSize;
      
      return {
        epoch,
        quote: roundTo(this.getAdjustedQuote(quote), precision),
        symbol,
        precision,
        pip_size: precision,
      };
    }
    
    // Fallback to original parser for other formats
    const candidates = [
      message,
      toObject(message.tick),
      toObject(message.data),
      toObject(message.message),
    ].filter(Boolean) as Record<string, unknown>[];

    for (const candidate of candidates) {
      const symbol = String(candidate.symbol ?? candidate.name ?? this.config.symbol);
      if (symbol !== this.config.symbol) continue;

      const quote =
        toNumber(candidate.quote) ??
        toNumber(candidate.price) ??
        toNumber(candidate.bid) ??
        toNumber(candidate.ask) ??
        toNumber(candidate.value);
      if (quote === null) continue;

      const epochRaw =
        toNumber(candidate.epoch) ??
        toNumber(candidate.epoch_ms) ??
        toNumber(candidate.time) ??
        toNumber(candidate.timestamp) ??
        toNumber(candidate.created_at);
      const epochBase = epochRaw ? Math.floor(epochRaw > 10_000_000_000 ? epochRaw / 1000 : epochRaw) : Math.floor(Date.now() / 1000);
      const epoch = this.getAdjustedEpoch(epochBase);
      const precision = toNumber(candidate.precision) ?? toNumber(candidate.pip_size) ?? this.config.pipSize;

      return {
        epoch,
        quote: roundTo(this.getAdjustedQuote(quote), precision),
        symbol,
        precision,
        pip_size: precision,
      };
    }

    return null;
  }

  private parseHistory(message: Record<string, unknown>): boolean {
    let historyMode: "replace" | "prepend" = this.pendingHistoryMode ?? "replace";

    // First, handle HFT19 response format: { "status": "ok", "code": 200, "data": { ... } }
    if (message.status === "ok" && message.data) {
      const data = message.data as Record<string, unknown>;
      const symbol = String(data.symbol ?? this.config.symbol);
      
      // Handle ticks response
      if (data.type === "ticks" && Array.isArray(data.ticks)) {
        const parsed: ChartDataPoint[] = [];
        let rawLatestEpoch: number | null = null;
        let rawLatestQuote: number | null = null;
        
        for (const tick of data.ticks) {
          const tickObj = toObject(tick);
          if (!tickObj) continue;
          
          const epochRaw = toNumber(tickObj.epoch);
          const quote = toNumber(tickObj.price);
          
          if (epochRaw !== null) {
            const epochBase = Math.floor(epochRaw > 10_000_000_000 ? epochRaw / 1000 : epochRaw);
            rawLatestEpoch = epochBase;
            if (quote !== null) rawLatestQuote = quote;
            
            parsed.push({
              epoch: this.getAdjustedEpoch(epochBase),
              quote: roundTo(this.getAdjustedQuote(quote ?? 0), this.config.pipSize),
              symbol,
              precision: this.config.pipSize,
              pip_size: this.config.pipSize,
            });
          }
        }
        
        if (rawLatestEpoch !== null) this.resolveMarketEpochOffset(rawLatestEpoch);
        if (rawLatestQuote !== null) this.resolveMarketQuoteOffset(rawLatestQuote);
        
        if (parsed.length > 0) {
          parsed.sort((a, b) => a.epoch - b.epoch);
          historyMode = this.pendingHistoryMode ?? historyMode;
          if (this.pendingHistoryMode === "prepend" && this.data.length > 0) {
            this.prependData(parsed);
          } else {
            this.replaceData(parsed);
          }
          this.notifyHistoryListeners(parsed, historyMode);
        }
        this.pendingHistoryMode = null;
        return true;
      }
      
      // Handle candles response
      if (data.type === "candles" && Array.isArray(data.candles)) {
        const parsed: ChartDataPoint[] = [];
        for (const candle of data.candles) {
          const candleObj = toObject(candle);
          if (!candleObj) continue;
          
          const epochRaw = toNumber(candleObj.epoch);
          const quote = toNumber(candleObj.close); // Use close price as the quote
          
          if (epochRaw !== null && quote !== null) {
            const epochBase = Math.floor(epochRaw > 10_000_000_000 ? epochRaw / 1000 : epochRaw);
            parsed.push({
              epoch: this.getAdjustedEpoch(epochBase),
              quote: roundTo(this.getAdjustedQuote(quote), this.config.pipSize),
              symbol,
              precision: this.config.pipSize,
              pip_size: this.config.pipSize,
            });
          }
        }
        
        if (parsed.length > 0) {
          parsed.sort((a, b) => a.epoch - b.epoch);
          historyMode = this.pendingHistoryMode ?? historyMode;
          if (this.pendingHistoryMode === "prepend" && this.data.length > 0) {
            this.prependData(parsed);
          } else {
            this.replaceData(parsed);
          }
          this.notifyHistoryListeners(parsed, historyMode);
        }
        this.pendingHistoryMode = null;
        return true;
      }
    }
    
    // Fallback to original parser for legacy formats
    const history =
      toObject(message.message) ??
      toObject(message.data) ??
      toObject(message.history) ??
      message;

    const series =
      (Array.isArray(history.ticks) && history.ticks) ||
      (Array.isArray(history.candles) && history.candles) ||
      (Array.isArray(history.ohlc) && history.ohlc) ||
      (Array.isArray(history.prices) && history.prices) ||
      (Array.isArray(history) ? history : null);

    if (!series) {
      return false;
    }

    const symbol = String(history.symbol ?? this.config.symbol);
    let rawLatestEpoch: number | null = null;
    let rawLatestQuote: number | null = null;
    for (const item of series) {
      const point = toObject(item);
      if (!point) continue;
      const epochRaw = toNumber(point.epoch) ?? toNumber(point.epoch_ms) ?? toNumber(point.time) ?? toNumber(point.timestamp);
      const quote =
        toNumber(point.quote) ??
        toNumber(point.close) ??
        toNumber(point.price) ??
        toNumber(point.bid) ??
        toNumber(point.ask) ??
        toNumber(point.value);
      if (epochRaw === null) continue;
      const epochBase = Math.floor(epochRaw > 10_000_000_000 ? epochRaw / 1000 : epochRaw);
      rawLatestEpoch = epochBase;
      if (quote !== null) rawLatestQuote = quote;
    }
    if (rawLatestEpoch !== null) this.resolveMarketEpochOffset(rawLatestEpoch);
    if (rawLatestQuote !== null) this.resolveMarketQuoteOffset(rawLatestQuote);

    const parsed: ChartDataPoint[] = [];
    for (const item of series) {
      const point = toObject(item);
      if (!point) continue;
      const quote =
        toNumber(point.quote) ??
        toNumber(point.close) ??
        toNumber(point.price) ??
        toNumber(point.bid) ??
        toNumber(point.ask) ??
        toNumber(point.value);
      const epochRaw = toNumber(point.epoch) ?? toNumber(point.epoch_ms) ?? toNumber(point.time) ?? toNumber(point.timestamp);
      if (quote === null || epochRaw === null) continue;
      const epochBase = Math.floor(epochRaw > 10_000_000_000 ? epochRaw / 1000 : epochRaw);
      parsed.push({
        epoch: this.getAdjustedEpoch(epochBase),
        quote: roundTo(this.getAdjustedQuote(quote), this.config.pipSize),
        symbol,
        precision: this.config.pipSize,
        pip_size: this.config.pipSize,
      });
    }

    if (parsed.length === 0) {
      this.pendingHistoryMode = null;
      return true;
    }
    parsed.sort((a, b) => a.epoch - b.epoch);
    historyMode = this.pendingHistoryMode ?? historyMode;
    if (this.pendingHistoryMode === "prepend" && this.data.length > 0) {
      this.prependData(parsed);
    } else {
      this.replaceData(parsed);
    }
    this.notifyHistoryListeners(parsed, historyMode);
    this.pendingHistoryMode = null;
    return true;
  }

  private addTick(next: ChartDataPoint): void {
    const last = this.data[this.data.length - 1];
    if (last && next.epoch < last.epoch) return;

    if (this.marketTransitionAnchorEpoch !== null && this.marketEpochOffset === null) {
      this.marketEpochOffset = this.marketTransitionAnchorEpoch - next.epoch;
    }

    if (last && next.epoch === last.epoch) {
      last.quote = next.quote;
      last.symbol = next.symbol;
      last.precision = next.precision ?? next.pip_size;
      last.pip_size = next.pip_size;
    } else {
      this.data.push(dataPointPool.acquire(next.epoch, next.quote, next.symbol, next.precision ?? next.pip_size ?? 0));
    }

    this.trimData();
    this.notifyListeners();
  }

  private replaceData(next: ChartDataPoint[]): void {
    for (const point of this.data) dataPointPool.release(point);
    this.data = next.map((point) => dataPointPool.acquire(point.epoch, point.quote, point.symbol, point.precision ?? point.pip_size ?? 0));
    this.trimData();
    this.notifyListeners();
  }

  private prependData(next: ChartDataPoint[]): void {
    const merged = new Map<number, ChartDataPoint>();
    for (const point of next) {
      merged.set(
        point.epoch,
        dataPointPool.acquire(point.epoch, point.quote, point.symbol, point.precision ?? point.pip_size ?? 0),
      );
    }
    for (const point of this.data) {
      merged.set(point.epoch, point);
    }
    this.data = Array.from(merged.values()).sort((a, b) => a.epoch - b.epoch);
    this.trimData();
    this.notifyListeners();
  }

  private trimData(): void {
    if (this.data.length <= this.config.maxDataPoints) return;
    const overflow = this.data.length - this.config.maxDataPoints;
    const removed = this.data.splice(0, overflow);
    for (const point of removed) dataPointPool.release(point);
  }

  private scheduleReconnect(): void {
    this.clearReconnectTimer();
    this.reconnectTimer = window.setTimeout(() => this.start(), this.config.reconnectMs);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer === null) return;
    window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  receive(raw: unknown): void {
    if (typeof raw === "string") {
      this.handleMessage(raw);
      return;
    }
    try {
      this.handleMessage(JSON.stringify(raw));
    } catch {
      // Ignore unstringifiable payloads.
    }
  }

  setData(points: Array<ChartDataInputPoint | ChartDataPoint>): void {
    const next = normalizeChartDataPoints(points, this.config.symbol, this.config.pipSize);
    this.replaceData(next);
  }

  append(point: ChartDataInputPoint | ChartDataPoint): void {
    this.addTick(normalizeChartDataPoint(point, this.config.symbol, this.config.pipSize));
  }

  appendMany(points: Array<ChartDataInputPoint | ChartDataPoint>): void {
    for (const point of normalizeChartDataPoints(points, this.config.symbol, this.config.pipSize)) {
      this.addTick(point);
    }
  }

  clearData(): void {
    this.replaceData([]);
  }

  setSymbols(symbols: LiveSymbol[]): void {
    this.symbols = [...symbols];
    this.applySymbolPrecision();
    this.notifyMarketListeners();
  }

  addListener(callback: (data: ChartDataPoint[]) => void): void {
    this.listeners.push(callback);
  }

  removeListener(callback: (data: ChartDataPoint[]) => void): void {
    const index = this.listeners.indexOf(callback);
    if (index > -1) this.listeners.splice(index, 1);
  }

  addHistoryListener(callback: (data: ChartDataPoint[], mode: "replace" | "prepend") => void): void {
    this.historyListeners.push(callback);
  }

  removeHistoryListener(callback: (data: ChartDataPoint[], mode: "replace" | "prepend") => void): void {
    const index = this.historyListeners.indexOf(callback);
    if (index > -1) this.historyListeners.splice(index, 1);
  }

  addMarketListener(callback: () => void): void {
    this.marketListeners.push(callback);
  }

  removeMarketListener(callback: () => void): void {
    const index = this.marketListeners.indexOf(callback);
    if (index > -1) this.marketListeners.splice(index, 1);
  }

  getSymbols(): LiveSymbol[] {
    return [...this.symbols];
  }

  getSelectedSymbol(): string {
    return this.config.symbol;
  }

  selectSymbol(symbol: string): void {
    if (!symbol || symbol === this.config.symbol) return;
    this.marketTransitionAnchorEpoch = null;
    this.marketTransitionAnchorQuote = null;
    this.marketEpochOffset = null;
    this.marketQuoteOffset = null;
    this.config.symbol = symbol;
    this.applySymbolPrecision();
    this.clearData();
    this.notifyMarketListeners();
    this.restartConnection();
  }

  private notifyListeners(): void {
    for (const callback of this.listeners) callback(this.data);
  }

  private notifyHistoryListeners(data: ChartDataPoint[], mode: "replace" | "prepend"): void {
    for (const callback of this.historyListeners) callback(data, mode);
  }

  private notifyMarketListeners(): void {
    for (const callback of this.marketListeners) callback();
  }

  getData(): ChartDataPoint[] {
    return this.data;
  }

  getLatestTick(): ChartDataPoint | null {
    return this.data.length ? this.data[this.data.length - 1] : null;
  }
}

export function createLiveDataFeed(config?: Partial<LiveDataOptions>): LiveDataFeed {
  return new LiveDataManager(config);
}

export function createLiveDataAdapter(config?: Partial<LiveDataOptions>): LiveDataAdapter {
  return new LiveDataManager(config) as LiveDataAdapter;
}

export const liveDataManager = createLiveDataFeed();
